import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Card,
    CardHeader,
    CardContent,
    FormControl,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    IconButton,
    Divider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Save as SaveIcon,
    ArrowForward as ArrowForwardIcon,
    Info as InfoIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { zoneService } from '../../../services/zoneService';
import { isZoneEngine } from '../../../services/rateEngine';
import ZoneCities from './ZoneCities';

const ZoneForm = ({ zone, onSuccess, onCancel, viewMode = false }) => {
    // Prefer the zone's own is_system flag over the cached engine setting: the
    // record itself is always current, whereas the cached value is only written
    // at login and would be stale for anyone whose session predates the change.
    const isDestinationZone = zone?.is_system ?? isZoneEngine();

    const { handleApiCallWithLoading, showError } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [regions, setRegions] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [regionsLoading, setRegionsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        region_pairs: []
    });
    const [errors, setErrors] = useState({});
    const [showHelp, setShowHelp] = useState(false);

    useEffect(() => {
        fetchRegions();
        
        if (zone) {
            setFormData({
                name: zone.name || '',
                code: zone.code || '',
                region_pairs: zone.region_pairs?.map(pair => ({
                    from_region: pair.from_region.id,
                    to_region: pair.to_region.id
                })) || []
            });
        }
    }, [zone]);

    const fetchRegions = async () => {
        setRegionsLoading(true);
        try {
            const response = await zoneService.getRegions();
            if (response.success) {
                setRegions(response.data);
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            showError('Failed to fetch regions. Please refresh the page.');
        } finally {
            setRegionsLoading(false);
        }
    };

    const validateRegionPairs = async (regionPairs) => {
        if (!regionPairs || regionPairs.length === 0) {
            return { success: true, errors: [] };
        }

        try {
            const response = await zoneService.validateRegionPairs(
                regionPairs,
                zone?.id || null
            );
            return response;
        } catch (error) {
            return { success: false, errors: [error.message || 'Validation failed'] };
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleRegionPairChange = (index, field, value) => {
        const newRegionPairs = [...formData.region_pairs];
        newRegionPairs[index] = {
            ...newRegionPairs[index],
            [field]: value
        };
        setFormData(prev => ({
            ...prev,
            region_pairs: newRegionPairs
        }));
    };

    const addRegionPair = () => {
        setFormData(prev => ({
            ...prev,
            region_pairs: [
                ...prev.region_pairs,
                { from_region: '', to_region: '' }
            ]
        }));
    };

    const removeRegionPair = (index) => {
        setFormData(prev => ({
            ...prev,
            region_pairs: prev.region_pairs.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Zone name is required';
        }
        
        if (formData.code && formData.code.length > 50) {
            newErrors.code = 'Zone code cannot exceed 50 characters';
        }

        // Basic region pair validation
        formData.region_pairs.forEach((pair, index) => {
            if (!pair.from_region) {
                newErrors[`region_pairs.${index}.from_region`] = 'From region is required';
            }
            if (!pair.to_region) {
                newErrors[`region_pairs.${index}.to_region`] = 'To region is required';
            }
            // Allow same-region mapping for intra-region deliveries
            // Removed restriction: if (pair.from_region === pair.to_region) { ... }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setValidationErrors([]);

        try {
            // Validate region pairs
            if (formData.region_pairs && formData.region_pairs.length > 0) {
                const validation = await validateRegionPairs(formData.region_pairs);
                if (!validation.success) {
                    setValidationErrors(validation.errors);
                    setLoading(false);
                    return;
                }
            }

            // Submit the form
            const zoneData = {
                name: formData.name,
                code: formData.code || null,
                region_pairs: formData.region_pairs || []
            };

            if (zone) {
                await zoneService.updateZone(zone.id, zoneData);
            } else {
                await zoneService.createZone(zoneData);
            }

            onSuccess();
        } catch (error) {
            console.error('Error during operation:', error);
            showError('Operation failed. Please try again.');
            
            // Handle validation errors
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(field => {
                    newErrors[field] = error.errors[field][0]; // Take first error message
                });
                setErrors(newErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    const getRegionName = (regionId) => {
        const region = regions.find(r => r.id === regionId);
        return region ? region.name : '';
    };

    const getAvailableToRegions = (fromRegionId, currentIndex) => {
        return regions.filter(region => {
            // Allow self-mapping (same region shipping) for intra-region deliveries
            // Removed restriction: if (region.id === fromRegionId) return false;
            
            // Check if this region is already used in other pairs
            for (let i = 0; i < formData.region_pairs.length; i++) {
                const pair = formData.region_pairs[i];
                if (i !== currentIndex) {
                    // Check for direct or reverse usage
                    if ((pair.from_region === region.id && pair.to_region === fromRegionId) ||
                        (pair.from_region === fromRegionId && pair.to_region === region.id)) {
                    return false;
                    }
                }
            }
            
            return true;
        });
    };

    const getRegionPairError = (index) => {
        const pair = formData.region_pairs[index];
        if (!pair) return null;

        // Check for circular reference
        const visited = new Set();
        const checkCircular = (currentFromRegion, targetRegion, path = []) => {
            if (visited.has(currentFromRegion)) return false;
            visited.add(currentFromRegion);

            for (const p of formData.region_pairs) {
                if (p.from_region === currentFromRegion) {
                    const newPath = [...path, currentFromRegion];
                    if (p.to_region === targetRegion) {
                        return { 
                            isCircular: true, 
                            path: [...newPath, targetRegion]
                        };
                    }
                    const result = checkCircular(p.to_region, targetRegion, newPath);
                    if (result.isCircular) return result;
                }
            }
            return { isCircular: false };
        };

        if (pair.from_region && pair.to_region) {
            // Allow self-mapping (same-region shipping) - skip circular check for A→A
            if (pair.from_region !== pair.to_region) {
                const circularCheck = checkCircular(pair.to_region, pair.from_region);
                if (circularCheck.isCircular) {
                    const pathStr = circularCheck.path
                        .map(regionId => getRegionName(regionId))
                        .join(' → ');
                    return {
                        type: 'circular',
                        message: "This pair would create a circular reference",
                        detail: `Circular path detected: ${pathStr}`
                    };
                }
            }

            // Check for reverse mapping
            const hasReverse = formData.region_pairs.some((p, i) => 
                i !== index && 
                p.from_region === pair.to_region && 
                p.to_region === pair.from_region
            );
            if (hasReverse) {
                return {
                    type: 'reverse',
                    message: "Reverse mapping detected",
                    detail: `A mapping from ${getRegionName(pair.to_region)} to ${getRegionName(pair.from_region)} already exists. Only one-way mappings are allowed.`
                };
            }
        }

        return null;
    };

    const RegionSelect = ({ 
        label, 
        value, 
        onChange, 
        options, 
        error, 
        helperText, 
        disabled = false,
        placeholder = "Search and select region"
    }) => {
        const selectedRegion = options.find(r => r.id === value) || null;

        return (
            <FormControl fullWidth error={error}>
                <Autocomplete
                    value={selectedRegion}
                    onChange={(event, newValue) => {
                        onChange(newValue ? newValue.id : '');
                    }}
                    options={options}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={label}
                            error={error}
                            helperText={helperText}
                            placeholder={placeholder}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <SearchIcon color="action" sx={{ ml: 1, mr: -0.5 }} />
                                        {params.InputProps.startAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                    disabled={disabled}
                    fullWidth
                    filterOptions={(options, { inputValue }) => {
                        const searchTerm = inputValue.toLowerCase();
                        return options.filter(option => 
                            option.name.toLowerCase().includes(searchTerm) ||
                            (option.code && option.code.toLowerCase().includes(searchTerm))
                        );
                    }}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <Box>
                                <Typography variant="body1">{option.name}</Typography>
                                {option.code && (
                                    <Typography variant="caption" color="textSecondary">
                                        Code: {option.code}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    )}
                    noOptionsText="No regions found"
                />
            </FormControl>
        );
    };

    return (
        <Box>
            {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Please fix the following issues:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {validationErrors.map((error, index) => (
                            <li key={index}>
                                <Typography variant="body2" component="div">
                                    {error}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            Note: Zone mappings must follow these rules:
                            <ul>
                                <li>Each region pair must be unique</li>
                                <li>Only one-way mappings are allowed (if A→B exists, B→A is not allowed)</li>
                                <li>No circular references (e.g., A→B→C→A is not allowed)</li>
                                <li>Same-region shipping is allowed (A→A) for intra-region deliveries</li>
                            </ul>
                        </Typography>
                    </Box>
                </Alert>
            )}

            <Card sx={{ mb: 2 }}>
                <CardHeader title="Zone Information" />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Zone Name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name}
                                disabled={viewMode}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Zone Code"
                                value={formData.code}
                                onChange={(e) => handleInputChange('code', e.target.value)}
                                error={!!errors.code}
                                helperText={errors.code}
                                disabled={viewMode}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Region pairs do not exist under the destination-only model: the
                zone comes from the destination city, so there is nothing to pair
                and no direction to keep symmetric. Cities are assigned to zones
                on this screen. */}
            {isDestinationZone ? (
                <ZoneCities zone={zone} viewMode={viewMode} />
            ) : (
            <Card>
                <CardHeader
                    title="Region Pairs"
                    subheader="Create one-way connections between regions. Each pair represents a valid delivery route. Same-region shipping (A→A) is supported for intra-region deliveries. Use the search to quickly find regions."
                    action={
                        !viewMode && (
                            <Button
                                startIcon={<AddIcon />}
                                onClick={addRegionPair}
                                disabled={loading}
                            >
                                Add Region Pair
                            </Button>
                        )
                    }
                />
                <CardContent>
                    {formData.region_pairs.map((pair, index) => {
                        const pairError = getRegionPairError(index);
                        return (
                            <Box key={index} sx={{ mb: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={5}>
                                        <RegionSelect
                                            label="From Region"
                                            value={pair.from_region}
                                            onChange={(value) => handleRegionPairChange(index, 'from_region', value)}
                                            options={regions}
                                            error={!!errors[`region_pairs.${index}.from_region`]}
                                            helperText={errors[`region_pairs.${index}.from_region`]}
                                                    disabled={viewMode}
                                        />
                                        </Grid>
                                    <Grid item xs={12} md={1} sx={{ textAlign: 'center' }}>
                                        <ArrowForwardIcon />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                        <RegionSelect
                                            label="To Region"
                                            value={pair.to_region}
                                            onChange={(value) => handleRegionPairChange(index, 'to_region', value)}
                                            options={getAvailableToRegions(pair.from_region, index)}
                                            error={!!errors[`region_pairs.${index}.to_region`]}
                                            helperText={errors[`region_pairs.${index}.to_region`]}
                                                    disabled={viewMode || !pair.from_region}
                                            placeholder={!pair.from_region ? "Select 'From Region' first" : "Search and select region"}
                                        />
                                        </Grid>
                                    {!viewMode && (
                                        <Grid item xs={12} md={1}>
                                                <IconButton
                                                onClick={() => removeRegionPair(index)}
                                                disabled={loading}
                                                    color="error"
                                                >
                                                    <RemoveIcon />
                                                </IconButton>
                                        </Grid>
                                    )}
                                    </Grid>
                                {pairError && (
                                    <Alert 
                                        severity="error" 
                                        sx={{ mt: 1 }}
                                        action={
                                            pairError.type === 'circular' && (
                                                <Tooltip title="This creates a loop in the delivery route">
                                                    <IconButton size="small" color="inherit">
                                                        <InfoIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        }
                                    >
                                        <Typography variant="body2" fontWeight="medium">
                                            {pairError.message}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {pairError.detail}
                                        </Typography>
                                    </Alert>
                                )}
                                {index < formData.region_pairs.length - 1 && (
                                    <Divider sx={{ my: 2 }} />
                                )}
                            </Box>
                        );
                    })}

                    {formData.region_pairs.length === 0 && !viewMode && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography color="textSecondary">
                                No region pairs added yet. Click "Add Region Pair" to create delivery routes.
                            </Typography>
                                </Box>
                    )}

                </CardContent>
            </Card>
            )}

            {!viewMode && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSubmit}
                        // Under the legacy model a zone is meaningless without at
                        // least one region pair; a destination zone needs none.
                        disabled={loading || (!isDestinationZone && formData.region_pairs.length === 0)}
                    >
                        {zone ? 'Update' : 'Create'} Zone
                    </Button>
                </Box>
            )}

            {/* Help Dialog */}
            <Dialog open={showHelp} onClose={() => setShowHelp(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Understanding Zone Mappings</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" paragraph>
                        Zone mappings define valid delivery routes between regions. Here are the key rules:
                    </Typography>
                    <Typography component="div" variant="body2">
                        <ul>
                            <li><strong>One-way Mappings:</strong> If you create a route from Region A to Region B, you cannot create a reverse route (B to A) in the same or different zone.</li>
                            <li><strong>No Circular References:</strong> You cannot create a circular path of regions. For example, if you have A→B and B→C, you cannot add C→A as it creates a loop.</li>
                            <li><strong>Unique Pairs:</strong> Each region pair must be unique within and across zones.</li>
                            <li><strong>Same-Region Shipping:</strong> You can create routes within the same region (A→A) for intra-region deliveries with specific rates.</li>
                        </ul>
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Examples:
                    </Typography>
                    <Typography component="div" variant="body2">
                        <ul>
                            <li>✅ Mumbai → Delhi, Delhi → Bangalore (Valid: Creates a linear path)</li>
                            <li>✅ Mumbai → Mumbai (Valid: Same-region/intra-region shipping)</li>
                            <li>❌ Mumbai → Delhi, Delhi → Mumbai (Invalid: Bidirectional)</li>
                            <li>❌ Mumbai → Delhi, Delhi → Bangalore, Bangalore → Mumbai (Invalid: Creates a loop)</li>
                        </ul>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowHelp(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZoneForm; 